import { useEffect, useState } from "react";
import { Button, Checkbox, Empty, Modal, Spin, Tag, message } from "antd";
import {
  getCustomerEmployeesForOwnerChange,
  reassignClientCustomerAccountOwner,
} from "../../services/ApiService";

// Opens whenever the Customer List page's Account Owner dropdown is
// changed to a real new owner (confirmed with the user, 2026-10-13) --
// NOT an immediate silent save, since onboard_forms.assign_to is a real,
// separately-tracked per-EMPLOYEE field (it drives "my assigned records"
// filtering and the onboarding digest elsewhere in this app), not just a
// display label. Lets the user pick exactly which of this customer's
// employees should move to the new owner too, rather than the two
// silently drifting apart.
const AccountOwnerChangeModal = ({
  open,
  customer,
  newOwnerId,
  newOwnerLabel,
  onClose,
  onSaved,
}) => {
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !customer) return;

    const fetchEmployees = async () => {
      setLoading(true);
      try {
        const res = await getCustomerEmployeesForOwnerChange(customer.id);
        if (res?.responseCode === 200) {
          const list = res.employees || [];
          setEmployees(list);
          // Pre-checked: employees currently on the customer's OLD owner
          // (and not exited) -- a clean single-owner customer needs zero
          // extra clicks, while employees already on a different owner
          // stay unchecked by default so they aren't silently swept up.
          setSelectedIds(
            new Set(
              list
                .filter(
                  (e) =>
                    !e.is_exited &&
                    e.current_owner_id &&
                    e.current_owner_id === customer.account_owner
                )
                .map((e) => e.onboard_form_id)
            )
          );
        } else {
          message.error(res?.responseMessage || "Could not load this customer's employees.");
        }
      } catch (error) {
        console.error("Error fetching customer employees:", error);
        message.error("Could not load this customer's employees.");
      } finally {
        setLoading(false);
      }
    };
    fetchEmployees();
  }, [open, customer]);

  const toggleEmployee = (onboardFormId, checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(onboardFormId);
      else next.delete(onboardFormId);
      return next;
    });
  };

  const handleConfirm = async () => {
    setSaving(true);
    try {
      const res = await reassignClientCustomerAccountOwner(customer.id, {
        new_owner_id: newOwnerId,
        employee_onboard_form_ids: Array.from(selectedIds),
      });
      if (res?.responseCode === 200) {
        message.success(
          selectedIds.size
            ? `Account owner updated — ${selectedIds.size} employee(s) moved to ${newOwnerLabel}.`
            : "Account owner updated."
        );
        onSaved(newOwnerId);
        onClose();
      } else {
        message.error(res?.responseMessage || "Failed to reassign account owner.");
      }
    } catch (error) {
      console.error("Error reassigning account owner:", error);
      message.error("Something went wrong. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={`Reassign Account Owner — ${customer?.client_customer_name || ""}`}
      open={open}
      onCancel={onClose}
      width={620}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button key="confirm" type="primary" loading={saving} onClick={handleConfirm}>
          Confirm — set to {newOwnerLabel}
        </Button>,
      ]}
    >
      <p className="owner-change-hint">
        New owner: <strong>{newOwnerLabel}</strong>. Pick which employees under this
        customer should also move to them — anyone left unchecked keeps their current
        owner. Exited employees can't be moved.
      </p>

      {loading ? (
        <div style={{ textAlign: "center", padding: "1.5rem" }}>
          <Spin />
        </div>
      ) : employees.length === 0 ? (
        <Empty description="No employees found under this customer." />
      ) : (
        <div className="owner-change-employee-list">
          {employees.map((emp) => (
            <div key={emp.onboard_form_id} className="owner-change-employee-row">
              <Checkbox
                checked={selectedIds.has(emp.onboard_form_id)}
                disabled={emp.is_exited}
                onChange={(e) => toggleEmployee(emp.onboard_form_id, e.target.checked)}
              >
                {emp.employee_name}
              </Checkbox>
              <Tag color={emp.is_exited ? "default" : "blue"}>{emp.status || "—"}</Tag>
              <span className="owner-change-current-owner">
                {emp.current_owner_name || "Unassigned"}
              </span>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
};

export default AccountOwnerChangeModal;
